import { useEffect, useRef } from 'react';
import styles from './SlideArtwork.module.css';

/**
 * SlideArtwork
 * Renderiza elementos visuales sobrepuestos sobre el fondo del slide,
 * cada uno con movimiento lento e independiente (efecto parallax / GTA 5 loading screen).
 *
 * Cada artwork en constants.js puede ser:
 *   { type: 'svg-figure', position, size, opacity, speed, direction, delay, figure }
 *   { type: 'image', src, position, size, opacity, speed, direction, delay }
 *
 * "figure": 'hanger' | 'mannequin' | 'dress' | 'bag' | 'tag' | 'star' | 'diamond'
 *
 * speed: 'slow' | 'medium' | 'fast'  (duración de la animación de drift)
 * direction: 'up' | 'down' | 'left' | 'right' | 'diagonal-ur' | 'diagonal-dl'
 */

/* ── Figuras SVG de moda ─────────────────────────────────── */
function HangerFigure({ color }) {
  return (
    <svg viewBox="0 0 120 100" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M60 8 C60 8 60 18 60 22" stroke={color} strokeWidth="3" strokeLinecap="round" />
      <circle cx="60" cy="6" r="5" stroke={color} strokeWidth="2.5" fill="none" />
      <path d="M60 22 C42 32 10 55 10 65 C10 70 14 74 20 74 L100 74 C106 74 110 70 110 65 C110 55 78 32 60 22Z" stroke={color} strokeWidth="2.5" fill="none" />
    </svg>
  );
}

function DressFigure({ color }) {
  return (
    <svg viewBox="0 0 100 140" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M35 10 L50 5 L65 10 L75 35 L55 40 L55 130 L45 130 L45 40 L25 35 Z" stroke={color} strokeWidth="2.5" strokeLinejoin="round" fill="none" />
      <path d="M45 40 L15 130 L85 130 L55 40" stroke={color} strokeWidth="2.5" strokeLinejoin="round" fill="none" />
      <path d="M35 10 C35 10 28 15 25 22" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <path d="M65 10 C65 10 72 15 75 22" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function BagFigure({ color }) {
  return (
    <svg viewBox="0 0 110 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="15" y="40" width="80" height="70" rx="6" stroke={color} strokeWidth="2.5" />
      <path d="M38 40 C38 40 38 22 55 22 C72 22 72 40 72 40" stroke={color} strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <line x1="15" y1="62" x2="95" y2="62" stroke={color} strokeWidth="1.5" />
      <circle cx="55" cy="78" r="5" stroke={color} strokeWidth="2" fill="none" />
    </svg>
  );
}

function TagFigure({ color }) {
  return (
    <svg viewBox="0 0 80 110" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M15 10 L65 10 L65 85 L40 100 L15 85 Z" stroke={color} strokeWidth="2.5" strokeLinejoin="round" />
      <circle cx="40" cy="24" r="6" stroke={color} strokeWidth="2" fill="none" />
      <line x1="28" y1="44" x2="52" y2="44" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <line x1="28" y1="56" x2="52" y2="56" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <line x1="28" y1="68" x2="45" y2="68" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function MannequinFigure({ color }) {
  return (
    <svg viewBox="0 0 100 180" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50" cy="18" r="14" stroke={color} strokeWidth="2.5" fill="none" />
      <path d="M36 32 C26 40 20 55 22 70 L78 70 C80 55 74 40 64 32 Z" stroke={color} strokeWidth="2.5" fill="none" />
      <path d="M22 52 L8 65 M78 52 L92 65" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
      <path d="M28 70 L22 130 M72 70 L78 130" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
      <line x1="50" y1="140" x2="50" y2="170" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
      <line x1="30" y1="170" x2="70" y2="170" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function DiamondFigure({ color }) {
  return (
    <svg viewBox="0 0 100 90" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M50 5 L95 35 L50 85 L5 35 Z" stroke={color} strokeWidth="2.5" strokeLinejoin="round" />
      <path d="M5 35 L30 35 L50 5 L70 35 L95 35" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M30 35 L50 85 M70 35 L50 85" stroke={color} strokeWidth="1.5" />
    </svg>
  );
}

function StarFigure({ color }) {
  return (
    <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M50 5 L61 35 L95 35 L68 57 L79 90 L50 70 L21 90 L32 57 L5 35 L39 35 Z"
        stroke={color} strokeWidth="2.5" strokeLinejoin="round" />
    </svg>
  );
}

const FIGURES = { hanger: HangerFigure, dress: DressFigure, bag: BagFigure, tag: TagFigure, mannequin: MannequinFigure, diamond: DiamondFigure, star: StarFigure };

const SPEED_MAP = { slow: '18s', medium: '11s', fast: '7s' };

const DIRECTION_MAP = {
  up:           'artDriftUp',
  down:         'artDriftDown',
  left:         'artDriftLeft',
  right:        'artDriftRight',
  'diagonal-ur':'artDriftDiagUR',
  'diagonal-dl':'artDriftDiagDL',
  float:        'artFloat',
};

export default function SlideArtwork({ artworks = [], active }) {
  return (
    <>
      {artworks.map((art, i) => {
        const animName = DIRECTION_MAP[art.direction || 'float'] || 'artFloat';
        const duration = SPEED_MAP[art.speed || 'slow'];

        const baseStyle = {
          position: 'absolute',
          width: art.size || 160,
          height: art.size || 160,
          left: art.position?.x ?? '60%',
          top: art.position?.y ?? '20%',
          opacity: active ? (art.opacity ?? 0.18) : 0,
          transition: `opacity 1s ease ${(art.delay || 0) + 0.3}s`,
          animation: `${animName} ${duration} ease-in-out infinite alternate`,
          animationDelay: `${art.delay || 0}s`,
          pointerEvents: 'none',
          zIndex: art.zIndex || 3,
          filter: art.blur ? `blur(${art.blur}px)` : undefined,
        };

        /* Imagen PNG/JPG externa */
        if (art.type === 'image') {
          return (
            <div key={i} style={baseStyle}>
              <img
                src={art.src}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </div>
          );
        }

        /* Figura SVG inline */
        const FigureComp = FIGURES[art.figure] || DiamondFigure;
        const figColor = art.color || 'rgba(255,255,255,0.9)';

        return (
          <div key={i} style={baseStyle} className={styles.artItem}>
            <FigureComp color={figColor} />
          </div>
        );
      })}
    </>
  );
}
